import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth, API, formatApiError } from "../auth";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Upload, X, ArrowLeft, ArrowRight, Check, Minus, Camera, ChevronsUpDown, RotateCcw } from "lucide-react";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { useFormDraft, usePhotoDraft } from "../hooks/useFormDraft";

const VISIT_TYPES = [
  "Rutina",
  "Auditoría",
  "Lanzamiento de producto",
  "Negociación",
  "Reposición",
  "Promocional",
  "Capacitación",
  "Otro",
];

import { todayISO } from "../utils/date";

export default function NewReport() {
  const { authHeaders, user, token } = useAuth();
  const navigate = useNavigate();
  const { id: editId } = useParams();
  const isEditing = Boolean(editId);

  const [brandCatalog, setBrandCatalog] = useState([]);
  const [loadingReport, setLoadingReport] = useState(isEditing);
  const draftKey = `draft:report:new`;
  const draftEnabled = !isEditing;
  const initialForm = {
    fecha: todayISO(),
    visitor_name: user?.name || "",
    visitor_position: user?.position || "",
    brand: "",
    chain: "",
    branch: "",
    city: "",
    visit_type: "Rutina",
    availability: null,
    visibility: null,
    price_info: "",
    additional_display: null,
    competitor_promo: null,
    competitor_promo_desc: "",
    expiring_products: null,
    merchandiser_task: "",
    comments: "",
  };
  const [form, setForm, { clearDraft: clearFormDraft, restored: formRestored }] = useFormDraft(draftKey, initialForm, draftEnabled, { ignoreKeys: ["fecha", "brand", "visitor_name", "visitor_position"] });
  const [photos, setPhotos, { clearDraft: clearPhotosDraft }] = usePhotoDraft(`draft:report:new:photos`, draftEnabled);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (formRestored) {
      // Force today's date; the draft may have been from a prior day.
      setForm((f) => (f.fecha === todayISO() ? f : { ...f, fecha: todayISO() }));
      toast.info("Borrador restaurado", {
        description: "Se recuperaron los datos que habías empezado a llenar.",
        duration: 5000,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formRestored]);

  const discardDraft = () => {
    if (!window.confirm("¿Descartar el borrador y empezar de cero?")) return;
    clearFormDraft();
    clearPhotosDraft();
    setForm(initialForm);
    setPhotos([]);
    toast.success("Borrador descartado");
  };

  // Load report for edit
  useEffect(() => {
    if (!isEditing) return;
    axios.get(`${API}/reports/${editId}`, { headers: authHeaders() })
      .then(({ data }) => {
        setForm({
          fecha: data.fecha,
          visitor_name: data.visitor_name || "",
          visitor_position: data.visitor_position || "",
          brand: data.brand || "",
          chain: data.chain || "",
          branch: data.branch || "",
          city: data.city || "",
          visit_type: data.visit_type || "Rutina",
          availability: data.availability ?? null,
          visibility: data.visibility ?? null,
          price_info: data.price_info || "",
          additional_display: data.additional_display ?? null,
          competitor_promo: data.competitor_promo ?? null,
          competitor_promo_desc: data.competitor_promo_desc || "",
          expiring_products: data.expiring_products ?? null,
          merchandiser_task: data.merchandiser_task || "",
          comments: data.comments || "",
        });
        setPhotos((data.photos || []).map((p) => ({
          localId: crypto.randomUUID(),
          path: p,
          preview: `${API}/files/${p}`,
          uploading: false,
          existing: true,
        })));
      })
      .catch(() => {
        toast.error("No se pudo cargar el reporte");
        navigate("/reports");
      })
      .finally(() => setLoadingReport(false));
  }, [editId]);

  useEffect(() => {
    axios.get(`${API}/brands`, { headers: authHeaders() })
      .then((r) => {
        setBrandCatalog(r.data);
        // Don't override a restored draft's brand.
        if (r.data.length > 0 && !formRestored) {
          setForm((f) => f.brand ? f : { ...f, brand: user?.brands?.[0] || r.data[0].name });
        }
      })
      .catch(() => {});
  }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const uploadFiles = async (files) => {
    for (const file of files) {
      const localId = crypto.randomUUID();
      const preview = URL.createObjectURL(file);
      setPhotos((p) => [...p, { localId, path: null, preview, uploading: true }]);
      try {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await axios.post(`${API}/upload`, fd, {
          headers: { ...authHeaders(), "Content-Type": "multipart/form-data" },
        });
        setPhotos((p) => p.map((x) => x.localId === localId ? { ...x, path: data.path, uploading: false } : x));
      } catch (e) {
        toast.error("Error al subir foto");
        setPhotos((p) => p.filter((x) => x.localId !== localId));
      }
    }
  };

  const onPickFiles = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length) uploadFiles(files);
    e.target.value = "";
  };

  const onDrop = (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files || []).filter((f) => f.type.startsWith("image/"));
    if (files.length) uploadFiles(files);
  };

  const removePhoto = (id) => setPhotos((p) => p.filter((x) => x.localId !== id));

  const submit = async (e) => {
    e.preventDefault();
    if (photos.some((p) => p.uploading)) {
      toast.warning("Espera a que terminen de subir las fotos");
      return;
    }
    // Validate required questions
    if (form.availability === null) { toast.error("Indica la disponibilidad de producto"); return; }
    if (form.visibility === null) { toast.error("Selecciona la visibilidad (1-5)"); return; }
    if (!form.price_info.trim()) { toast.error("Ingresa precio al consumidor"); return; }
    if (form.additional_display === null) { toast.error("Indica si hay exhibición o espacio adicional"); return; }
    if (form.competitor_promo === null) { toast.error("Indica si la competencia tiene promoción"); return; }
    if (form.expiring_products === null) { toast.error("Indica si hay productos próximos a vencer o vencidos"); return; }

    setSubmitting(true);
    try {
      const payload = { ...form, photos: photos.map((p) => p.path).filter(Boolean) };
      if (isEditing) {
        await axios.put(`${API}/reports/${editId}`, payload, { headers: authHeaders() });
        toast.success("Reporte actualizado");
        navigate(`/reports/${editId}`);
      } else {
        await axios.post(`${API}/reports`, payload, { headers: authHeaders() });
        clearFormDraft();
        clearPhotosDraft();
        toast.success("Reporte guardado");
        navigate("/reports");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-5 md:p-8 lg:p-12 max-w-5xl mx-auto" data-testid="new-report-page">
      <button onClick={() => navigate(-1)} className="kbd-label flex items-center gap-2 mb-6 text-slate-500 hover:text-slate-900" data-testid="back-btn">
        <ArrowLeft className="w-4 h-4" /> Volver
      </button>
      <div className="mb-8">
        <div className="kbd-label mb-2 text-emerald-700">/ {isEditing ? "Editar reporte" : "Nuevo reporte"}</div>
        <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-slate-950">
          {isEditing ? "Editar visita" : "Reporte de visita"}
        </h1>
      </div>

      {loadingReport ? (
        <div className="bg-white border border-slate-200 rounded-lg p-16 text-center kbd-label text-slate-400">Cargando…</div>
      ) : (
      <form onSubmit={submit} className="space-y-6" data-testid="report-form">
        {formRestored && !isEditing && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3" data-testid="draft-restored-banner">
            <RotateCcw className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-display font-semibold text-slate-900 text-sm">Borrador recuperado</div>
              <div className="text-xs text-slate-600 mt-0.5">Continuaste un reporte que dejaste a medias. Tus datos y fotos ya subidas se restauraron.</div>
            </div>
            <button type="button" onClick={discardDraft} className="kbd-label text-amber-800 hover:text-amber-950 underline underline-offset-4" data-testid="discard-draft-btn">
              Descartar
            </button>
          </div>
        )}
        <Section title="Información general" step="01">
          <div className="grid md:grid-cols-2 gap-5">
            <Field label="Fecha" required>
              <Input
                type="date"
                value={form.fecha}
                readOnly
                disabled
                className="bg-slate-100 text-slate-700 cursor-not-allowed"
                data-testid="field-fecha"
              />
              <p className="text-[11px] text-slate-500 mt-1.5">Se llena automáticamente con la fecha de hoy</p>
            </Field>
            <Field label="Tipo de visita">
              <Select value={form.visit_type} onValueChange={(v) => update("visit_type", v)}>
                <SelectTrigger data-testid="field-visit-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {VISIT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Nombre de la persona que visita" required>
              <Input value={form.visitor_name} onChange={(e) => update("visitor_name", e.target.value)} required data-testid="field-visitor" />
            </Field>
            <Field label="Cargo de la persona que visita">
              <Input value={form.visitor_position} onChange={(e) => update("visitor_position", e.target.value)} placeholder="Ej: Gerente de marca, Supervisor, Merchandiser" data-testid="field-visitor-position" />
            </Field>
            <Field label="Marca" required span>
              {brandCatalog.length > 0 ? (
                <BrandCombobox
                  value={form.brand}
                  onChange={(v) => update("brand", v)}
                  brands={brandCatalog}
                />
              ) : (
                <Input value={form.brand} onChange={(e) => update("brand", e.target.value)} required placeholder="Nombre de la marca" data-testid="field-brand" />
              )}
            </Field>
          </div>
        </Section>

        <Section title="Punto de venta" step="02">
          <div className="grid md:grid-cols-2 gap-5">
            <Field label="Cadena / Cliente" required>
              <Input value={form.chain} onChange={(e) => update("chain", e.target.value)} required data-testid="field-chain" />
            </Field>
            <Field label="Sucursal / PDV" required>
              <Input value={form.branch} onChange={(e) => update("branch", e.target.value)} required data-testid="field-branch" />
            </Field>
            <Field label="Ciudad">
              <Input value={form.city} onChange={(e) => update("city", e.target.value)} data-testid="field-city" />
            </Field>
          </div>
        </Section>

        <Section title="Evaluación de la visita" step="03">
          <div className="space-y-6">
            {/* Disponibilidad de producto */}
            <YesNo
              label="Disponibilidad de Producto"
              required
              value={form.availability}
              onChange={(v) => update("availability", v)}
              testId="field-availability"
            />

            {/* Visibilidad 1-5 */}
            <div>
              <Label className="kbd-label">Visibilidad <span className="text-red-500 ml-1">*</span></Label>
              <div className="mt-3">
                <div className="flex items-center justify-evenly gap-1 sm:gap-2 max-w-md mx-auto">
                  {[1, 2, 3, 4, 5].map((n) => {
                    const active = form.visibility === n;
                    return (
                      <button
                        key={n}
                        type="button"
                        onClick={() => update("visibility", n)}
                        className={`w-12 h-12 sm:w-14 sm:h-14 rounded-full font-display font-bold text-lg border-2 transition-all ${
                          active
                            ? "bg-emerald-700 border-emerald-700 text-white scale-110 shadow-md"
                            : "bg-white border-slate-300 text-slate-700 hover:border-emerald-500"
                        }`}
                        data-testid={`field-visibility-${n}`}
                      >
                        {n}
                      </button>
                    );
                  })}
                </div>
                <div className="flex items-center justify-between mt-2 max-w-md mx-auto px-1">
                  <span className="text-[11px] text-slate-500 font-medium">Mala</span>
                  <span className="text-[11px] text-slate-500 font-medium">Excelente</span>
                </div>
              </div>
            </div>

            {/* Precio al consumidor */}
            <div>
              <Label className="kbd-label">Precio al Consumidor (Marca; Empaque; Precio) <span className="text-red-500 ml-1">*</span></Label>
              <Textarea
                rows={3}
                value={form.price_info}
                onChange={(e) => update("price_info", e.target.value)}
                placeholder="Ej: Coca-Cola; 2L; $2.50&#10;Pepsi; 2L; $2.40"
                className="mt-2"
                data-testid="field-price-info"
              />
            </div>

            {/* Exhibición o espacio adicional */}
            <YesNo
              label="Exhibición o Espacio Adicional"
              required
              value={form.additional_display}
              onChange={(v) => update("additional_display", v)}
              testId="field-additional-display"
            />

            {/* Competencia tiene promoción */}
            <YesNo
              label="¿Nuestra Competencia Tiene Promoción?"
              required
              value={form.competitor_promo}
              onChange={(v) => update("competitor_promo", v)}
              testId="field-competitor-promo"
            />

            {/* Describa promoción */}
            <div>
              <Label className="kbd-label">Describa La Promoción de La Competencia</Label>
              <Textarea
                rows={3}
                value={form.competitor_promo_desc}
                onChange={(e) => update("competitor_promo_desc", e.target.value)}
                placeholder="Marca · Tipo de promoción · Mecánica..."
                className="mt-2"
                data-testid="field-competitor-promo-desc"
              />
            </div>

            {/* Productos próximos a vencer */}
            <YesNo
              label="¿Hay productos próximos a vencer o vencidos?"
              required
              value={form.expiring_products}
              onChange={(v) => update("expiring_products", v)}
              testId="field-expiring-products"
            />

            {/* Tarea / recomendación para el mercaderista */}
            <div>
              <Label className="kbd-label">¿Hay una tarea asignada o alguna recomendación para el mercaderista?</Label>
              <Textarea
                rows={3}
                value={form.merchandiser_task}
                onChange={(e) => update("merchandiser_task", e.target.value)}
                placeholder="Ej: Reponer empaques de 600ml, revisar planograma, limpiar exhibidor..."
                className="mt-2"
                data-testid="field-merchandiser-task"
              />
            </div>

            {/* Comentarios extra */}
            <div>
              <Label className="kbd-label">Comentarios adicionales</Label>
              <Textarea
                rows={2}
                value={form.comments}
                onChange={(e) => update("comments", e.target.value)}
                className="mt-2"
                data-testid="field-comments"
              />
            </div>
          </div>
        </Section>

        <Section title="Fotografías" step="04" hint="Puedes tomar fotos con la cámara o subir desde galería. Máximo 10MB c/u.">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {/* Take photo with camera */}
            <label
              htmlFor="camera-input"
              className="flex flex-col items-center justify-center border-2 border-dashed border-emerald-300 bg-emerald-50/50 hover:border-emerald-600 hover:bg-emerald-50 transition-colors rounded-lg p-6 cursor-pointer text-center"
              data-testid="camera-trigger"
            >
              <Camera className="w-8 h-8 text-emerald-700 mb-2" />
              <div className="font-display font-semibold text-slate-900">Tomar foto</div>
              <div className="kbd-label mt-1">Cámara del dispositivo</div>
              <input
                id="camera-input"
                type="file"
                accept="image/*"
                capture="environment"
                multiple
                onChange={onPickFiles}
                className="hidden"
                data-testid="camera-input"
              />
            </label>

            {/* Pick from gallery / drag-and-drop */}
            <label
              htmlFor="photo-input"
              onDragOver={(e) => e.preventDefault()}
              onDrop={onDrop}
              className="flex flex-col items-center justify-center border-2 border-dashed border-slate-300 hover:border-emerald-600 hover:bg-emerald-50/40 transition-colors rounded-lg p-6 cursor-pointer text-center"
              data-testid="photo-dropzone"
            >
              <Upload className="w-8 h-8 text-slate-400 mb-2" />
              <div className="font-display font-semibold text-slate-900">Galería o archivo</div>
              <div className="kbd-label mt-1">JPG · PNG · WEBP · HEIC</div>
              <input
                id="photo-input"
                type="file"
                accept="image/*"
                multiple
                onChange={onPickFiles}
                className="hidden"
                data-testid="photo-input"
              />
            </label>
          </div>

          {photos.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 mt-5">
              {photos.map((p) => (
                <div key={p.localId} className="relative aspect-square bg-slate-100 rounded-md overflow-hidden group">
                  <img src={p.preview} alt="preview" className="w-full h-full object-cover" />
                  {p.uploading && (
                    <div className="absolute inset-0 bg-slate-950/50 flex items-center justify-center">
                      <div className="kbd-label !text-white">Subiendo…</div>
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => removePhoto(p.localId)}
                    className="absolute top-2 right-2 w-7 h-7 bg-white rounded-full flex items-center justify-center shadow opacity-0 group-hover:opacity-100 transition-opacity"
                    data-testid={`remove-photo-${p.localId}`}
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </Section>

        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 pt-4 sticky bottom-0 bg-slate-50 p-4 -mx-5 md:-mx-8 lg:-mx-12 border-t border-slate-200">
          <Button type="button" variant="outline" onClick={() => navigate(-1)} className="border-slate-300" data-testid="cancel-btn">
            Cancelar
          </Button>
          <Button type="submit" disabled={submitting} className="bg-emerald-700 hover:bg-emerald-800 text-white px-6 group" data-testid="submit-report-btn">
            {submitting ? (isEditing ? "Actualizando…" : "Guardando…") : (isEditing ? "Actualizar reporte" : "Guardar reporte")}
            <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Button>
        </div>
      </form>
      )}
    </div>
  );
}

function Section({ title, step, hint, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5 md:p-7">
      <div className="flex items-start gap-4 mb-5 pb-4 border-b border-slate-100">
        <span className="kbd-label font-mono-tv bg-emerald-700 text-white px-2 py-1">{step}</span>
        <div>
          <h3 className="font-display text-xl font-bold text-slate-950">{title}</h3>
          {hint && <p className="text-sm text-slate-500 mt-1">{hint}</p>}
        </div>
      </div>
      {children}
    </div>
  );
}

function Field({ label, required, span, children }) {
  return (
    <div className={span ? "md:col-span-2" : ""}>
      <Label className="kbd-label">{label}{required && <span className="text-red-500 ml-1">*</span>}</Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}

function YesNo({ label, required, value, onChange, testId }) {
  const opts = [
    { v: true, label: "Sí", icon: Check, color: "emerald" },
    { v: false, label: "No", icon: Minus, color: "slate" },
  ];
  return (
    <div>
      <Label className="kbd-label">{label}{required && <span className="text-red-500 ml-1">*</span>}</Label>
      <div className="mt-2 grid grid-cols-2 gap-3 max-w-md">
        {opts.map((o) => {
          const active = value === o.v;
          const Icon = o.icon;
          return (
            <button
              key={String(o.v)}
              type="button"
              onClick={() => onChange(o.v)}
              className={`flex items-center justify-center gap-2 py-3 rounded-md border-2 font-medium transition-all ${
                active
                  ? o.color === "emerald"
                    ? "bg-emerald-700 border-emerald-700 text-white shadow-md"
                    : "bg-slate-900 border-slate-900 text-white shadow-md"
                  : "bg-white border-slate-300 text-slate-700 hover:border-slate-500"
              }`}
              data-testid={`${testId}-${o.v ? "yes" : "no"}`}
            >
              <Icon className="w-4 h-4" /> {o.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function BrandCombobox({ value, onChange, brands }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          className="w-full h-10 px-3 flex items-center justify-between rounded-md border border-input bg-white text-sm hover:border-emerald-600 focus:outline-none focus:ring-2 focus:ring-emerald-200"
          data-testid="field-brand"
        >
          <span className={value ? "text-slate-900 truncate" : "text-slate-400"}>
            {value || "Selecciona o busca una marca…"}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 text-slate-400" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command>
          <CommandInput placeholder="Escribe las primeras letras…" data-testid="brand-combobox-input" />
          <CommandList>
            <CommandEmpty>No se encontró ninguna marca</CommandEmpty>
            <CommandGroup>
              {brands.map((b) => (
                <CommandItem
                  key={b.id}
                  value={b.name}
                  onSelect={(currentValue) => {
                    // currentValue may be lowercased by cmdk; resolve to original
                    const match = brands.find((x) => x.name.toLowerCase() === currentValue.toLowerCase());
                    onChange(match ? match.name : currentValue);
                    setOpen(false);
                  }}
                  data-testid={`brand-option-${b.id}`}
                >
                  <Check className={`mr-2 h-4 w-4 ${value === b.name ? "opacity-100 text-emerald-600" : "opacity-0"}`} />
                  {b.name}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
